import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ShoppingBag, ExternalLink, Sparkles, Search, ArrowRight, Tag, CheckCircle2, XCircle } from "lucide-react";

function fmtPrice(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function ProductCard({ p }) {
  return (
    <a
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      className="jj-card overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
      data-testid={`shop-product-${p.id}`}
    >
      <div className="relative aspect-square bg-cream-surface overflow-hidden">
        {p.image ? (
          <img
            src={p.image}
            alt={p.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--jj-muted)]">
            <ShoppingBag className="h-10 w-10" strokeWidth={1.2} />
          </div>
        )}
        {p.compare_at && p.compare_at > (p.price || 0) && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gold text-jjblue text-[10px] font-semibold tracking-wide px-2 py-1 rounded-full uppercase">
            <Tag className="h-3 w-3" /> Sale
          </span>
        )}
        {!p.in_stock && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-jjblue/90 text-cream text-[10px] font-semibold tracking-wide px-2 py-1 rounded-full uppercase">
            Sold out
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">{p.product_type}</div>
        <h3 className="font-serif text-lg text-jjblue mt-1 leading-tight line-clamp-2" data-testid={`shop-product-title-${p.id}`}>{p.title}</h3>
        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="text-burgundy font-serif text-xl">
            {fmtPrice(p.price)}
            {p.compare_at && p.compare_at > (p.price || 0) && (
              <span className="ml-2 text-xs text-[var(--jj-muted)] line-through font-sans">{fmtPrice(p.compare_at)}</span>
            )}
          </div>
          <span className="text-xs text-burgundy inline-flex items-center gap-1 opacity-70 group-hover:opacity-100">
            View <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function Shop() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shop/products");
        if (alive) setData(data);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const products = data?.products || [];
  const categories = data?.categories || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "all" && p.product_type !== category) return false;
      if (!q) return true;
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.product_type || "").toLowerCase().includes(q) ||
        (p.tags || []).join(" ").toLowerCase().includes(q)
      );
    });
  }, [products, category, query]);

  const featured = useMemo(() => products.slice(0, 3), [products]);

  return (
    <div className="bg-cream">
      <Seo
        title="The Shop · Wear the Word · Jesus Just Us"
        description="Faith-forward apparel, journals, mugs, blankets, and gifts from the official Jesus Just Us Shopify store. Wear it. Gift it. Sow into the Kingdom."
        path="/shop"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-jj">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="overline text-burgundy flex items-center gap-2">
              <ShoppingBag className="h-3.5 w-3.5" /> The Shop
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-jjblue mt-3 leading-[1.04] tracking-tight">
              Wear the Word.<br /><span className="text-burgundy italic">Carry the Kingdom.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[var(--jj-text-secondary)] leading-relaxed">
              Apparel, journals, mugs, blankets, and gifts from the official JJU Shopify store. Every purchase sows back into the work — and looks beautiful while doing it.
            </p>
            <div className="mt-7 flex items-center gap-3 flex-wrap">
              {data?.store_url && (
                <a
                  href={data.store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="shop-open-store"
                >
                  <ShoppingBag className="h-4 w-4" /> Open the full store <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <span className="text-xs text-[var(--jj-muted)] inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-gold" /> {data?.total ?? "—"} pieces available
              </span>
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-3 gap-3">
            {featured.map((p, i) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`relative aspect-square rounded-2xl overflow-hidden ring-1 ring-jj hover:ring-[var(--jj-gold)] transition-all duration-300 hover:-translate-y-0.5 ${i === 1 ? "row-span-2 aspect-auto" : ""}`}
                data-testid={`shop-hero-${p.id}`}
              >
                {p.image && <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover" />}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-jjblue/85 to-transparent">
                  <div className="text-cream text-xs font-medium line-clamp-1">{p.title}</div>
                  <div className="text-cream/85 text-[11px]">{fmtPrice(p.price)}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 flex-wrap" data-testid="shop-categories">
            <button
              onClick={() => setCategory("all")}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${category === "all" ? "bg-burgundy text-cream" : "bg-cream-surface text-jjblue/80 hover:text-burgundy border border-jj"}`}
              data-testid="shop-category-all"
            >
              All ({products.length})
            </button>
            {categories.slice(0, 10).map((c) => (
              <button
                key={c.name}
                onClick={() => setCategory(c.name)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${category === c.name ? "bg-burgundy text-cream" : "bg-cream-surface text-jjblue/80 hover:text-burgundy border border-jj"}`}
                data-testid={`shop-category-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c.name} ({c.count})
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--jj-muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the shop…"
              className="input-jj pl-10"
              data-testid="shop-search"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="jj-card overflow-hidden">
                <div className="aspect-square bg-cream-surface animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-1/2 bg-cream-surface rounded animate-pulse" />
                  <div className="h-5 w-4/5 bg-cream-surface rounded animate-pulse" />
                  <div className="h-5 w-1/3 bg-cream-surface rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16" data-testid="shop-error">
            <XCircle className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.4} />
            <p className="mt-4 text-[var(--jj-text-secondary)] max-w-md mx-auto">The Shopify store couldn't be reached right now. You can{" "}
              <a href="https://jesus-just-us.myshopify.com" target="_blank" rel="noopener noreferrer" className="text-burgundy font-medium hover:underline">visit the full store directly</a>.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[var(--jj-muted)]" data-testid="shop-empty">
            Nothing matches that filter — try clearing the search or picking another category.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="shop-grid">
            {filtered.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}

        {!loading && !error && (
          <div className="mt-10 text-center">
            <a
              href={data?.store_url || "https://jesus-just-us.myshopify.com"}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="shop-bottom-cta"
            >
              See everything in the Shopify store <ExternalLink className="h-4 w-4" />
            </a>
            <p className="mt-4 text-xs text-[var(--jj-muted)] inline-flex items-center gap-1.5 justify-center">
              <CheckCircle2 className="h-3 w-3 text-gold" /> Secure checkout powered by Shopify · Shipping handled by JJU
            </p>
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <p className="text-sm text-[var(--jj-muted)]">
          Part of the <Link to="/family" className="text-burgundy font-medium hover:underline">Jesus Just Us family</Link> of Kingdom properties.
        </p>
      </section>
    </div>
  );
}

/**
 * Compact "Featured from the Shop" strip that any page can drop in.
 * Shows 4 in-stock items. Lazy-loads from /api/shop/products (cached on backend).
 */
export function ShopFeatured({ limit = 4 }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shop/products");
        if (alive) setProducts((data?.products || []).filter((p) => p.in_stock).slice(0, limit));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [limit]);

  if (products.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="landing-shop-featured">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <div className="overline text-burgundy flex items-center gap-2">
            <ShoppingBag className="h-3.5 w-3.5" /> The Shop
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-jjblue mt-3 leading-tight">Wear the Word.</h2>
          <p className="mt-3 text-[var(--jj-text-secondary)] max-w-xl">
            Branded apparel, gifts, journals, and home goods from the JJU Shopify store. Every purchase sows back into the work.
          </p>
        </div>
        <Link to="/shop" className="text-sm text-burgundy hover:underline inline-flex items-center gap-1" data-testid="landing-shop-all">
          Browse the shop <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {products.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}
